import { LoadingState } from '../ui';
// src/pages/Dashboard.jsx

import { useEffect, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { supabase } from "../lib/supabaseClient";
import notify from "../lib/notify";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import ShareButton from "../components/ShareButton";
import CompetitionCard from "../components/CompetitionCard";
import CompetitionCarousel from "../components/CompetitionCarousel";
import TopGafferConfig from "../components/TopGafferConfig";
import MultiLeagueConfig from "../components/MultiLeagueConfig";
import {DashboardNextMatchday, DashboardGames, DashboardDiscovery} from '../components/DashboardPlay';
import {loadDashboardPlay, isDashboardActive, isDashboardEnded, dashboardFinishedRows, dashboardPontoonEligible, entryName, entryRoute, dashboardResultsRoute} from '../lib/dashboardPlay';
import { PONTOON_ENABLED } from "../lib/featureFlags";

// Leagues selectable when creating a competition (matches the single-league dropdown).
const LEAGUE_OPTIONS = [
  "Premier League", "Championship", "La Liga", "Serie A", "Ligue 1",
  "Bundesliga", "League One", "League Two", "MLS",
];

// Phase 5 (multi-league) kill switch. Keep false in production until the 5d
// engine (round advancement + eliminations) is built and rehearsed. While false,
// the creation toggle is hidden so no multi-league competition can be created;
// all multi-league code paths stay dormant. Flip to true to re-enable.
const MULTI_LEAGUE_ENABLED = false;

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isAtLeastAge } from "../lib/ageUtils";
import { Button as FcButton, Card as FcCard, Input as FcInput, LinkButton as FcLinkButton, Select as FcSelect, Table as FcTable } from '../ui';
import { Dialog as FcDialog } from '../ui';
import {SegmentedControl} from '../ui';

export default function Dashboard({ session, userRole, username }) {
  const isAlreadyRegisteredError = (error) => {
    if (!error) return false;
    return error.code === "23505" || (error.message || "").toLowerCase().includes("duplicate key");
  };

  const [searchParams] = useSearchParams();
  const [upcoming, setUpcoming] = useState([]);
  const [registered, setRegistered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playData, setPlayData] = useState({});
  const [gamesFailed, setGamesFailed] = useState(false);
  const [discoveryFailed, setDiscoveryFailed] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const update = () => setNow(Date.now());
    const timer = setInterval(update, 1000);
    window.addEventListener('focus', update);
    return () => {clearInterval(timer); window.removeEventListener('focus', update);};
  }, []);
  const [showPrivateCompModal, setShowPrivateCompModal] = useState(false);
  const [privateCompForm, setPrivateCompForm] = useState({
    name: "",
    league: "",
    season: "",
    type: "",
    knockoutType: "elimination",
    startDate: "",
    startMatchDay: "",
    is_18_plus: false,
    inviteSquads: false,
    selectedSquads: [],
    topGaffer: { enabled: false, stageCount: 1, stages: [] },
    multiLeague: { enabled: false, leagues: [], rounds: [] },
    pontoon: { enabled: false, matchDays: "" },
  });
  const [privateCompKey, setPrivateCompKey] = useState("");
  const [creatingPrivateComp, setCreatingPrivateComp] = useState(false);
  const [myPrivateComps, setMyPrivateComps] = useState([]);
  // Multi-entry: which entry is shown per competition row on the dashboard.
  const [dashEntryByComp, setDashEntryByComp] = useState({});
  // Phase 3: entries awaiting approval per competition the user manages.
  const [pendingApprovalsByComp, setPendingApprovalsByComp] = useState({});
  const [pendingApprovalSummary, setPendingApprovalSummary] = useState([]); // [{competition_id, name, type, count}]
  const [userReferralCode, setUserReferralCode] = useState("");
  const [userSquads, setUserSquads] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [futureMatchdays, setFutureMatchdays] = useState([]);
  const [recentlyFinished, setRecentlyFinished] = useState([]);
  
  // Filter states for competitions
  const [registeredFilter, setRegisteredFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [privateFilter, setPrivateFilter] = useState('active'); // 'active', 'inactive', 'all'
  
  // Win statistics (fetched separately for accuracy)
  const [winStats, setWinStats] = useState({ seasonWins: null, knockoutWins: null });
  
  // Fetch accurate win statistics from trophy room view
  const fetchWinStats = async () => {
    if (!session?.user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('trophy_room_stats_view')
        .select('competition_type, is_winner')
        .eq('user_id', session.user.id)
        .eq('is_winner', true)
        .neq('competition_type', 'scout');
      
      if (!error && data) {
        const seasonWins = data.filter(comp => comp.competition_type === 'season').length;
        const knockoutWins = data.filter(comp => comp.competition_type === 'knockout').length;
        setWinStats({ seasonWins, knockoutWins });
      }
    } catch (err) {
    }
  };
  
  const { seasonWins, knockoutWins } = winStats;

  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const userMeetsCompetitionAgeRequirement = async (userId, comp) => {
    if (!comp?.is_18_plus) {
      return { allowed: true };
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('date_of_birth')
      .eq('id', userId)
      .maybeSingle();

    const isAdult = isAtLeastAge(profile?.date_of_birth, 18);
    if (!isAdult) {
      return { allowed: false, message: 'This competition is 18+ only.' };
    }

    return { allowed: true };
  };

  // Helper function to filter competitions based on competition status
  const filterCompetitions = (competitions, filterType) => {
    return competitions.filter(comp => {
      switch (filterType) {
        case 'active':
          // Filter by competition status - active competitions
          return isDashboardActive(comp);
        case 'inactive':
          // Filter by competition status - inactive competitions
          return !isDashboardActive(comp);
        case 'all':
          return true;
        default:
          return true;
      }
    });
  };

  // Helper function to sort competitions by selection_status A-Z, then name A-Z
  const sortCompetitions = (competitions) => {
    return [...competitions].sort((a, b) => {
      // First sort by selection_status A-Z
      // For registered competitions, use selection_status; for private competitions, use status
      const aStatus = a.selection_status || a.status || '';
      const bStatus = b.selection_status || b.status || '';
      const statusComparison = aStatus.localeCompare(bStatus);
      if (statusComparison !== 0) {
        return statusComparison;
      }
      
      // If selection_status is the same, sort by competition name A-Z
      return (a.competition || a.name || '').localeCompare(b.competition || b.name || '');
    });
  };

  const getCompetitionRoute = (competitionId, competitionType) =>
    competitionType === 'world_stage'
      ? `/world-stage/${competitionId}`
      : `/competition/${competitionId}`;

  // Get filtered and sorted competitions
  const filteredRegistered = sortCompetitions(filterCompetitions(registered, registeredFilter));

  // Multi-entry: collapse the per-entry rows into one row per competition. Each
  // group keeps its entries so a dropdown can switch which entry the row shows.
  const groupedRegistered = (() => {
    const map = new Map();
    for (const row of filteredRegistered) {
      const key = row.competition_id || row.competition;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return Array.from(map.values()).map((rows) => {
      const entries = [...rows].sort((a, b) => (a.entry_index || 1) - (b.entry_index || 1));
      return { competition_id: entries[0].competition_id, entries };
    });
  })();
  const selectedRowFor = (group) => {
    const sel = dashEntryByComp[group.competition_id];
    return group.entries.find((e) => e.entry_id === sel) || group.entries[0];
  };
  const setDashEntry = (compId, entryId) =>
    setDashEntryByComp((prev) => ({ ...prev, [compId]: entryId }));
  // Source-entry eligibility is separate from the game's and parent's lifecycle.
  const pontoonEntries = PONTOON_ENABLED ? registered.filter(r=>r.competition_type==='pontoon') : [];
  const pontoonGames = pontoonEntries.filter(row=>dashboardPontoonEligible(playData[row.entry_id]));
  const hasEndedPontoonGames = pontoonEntries.some(isDashboardEnded);
  const pastPontoonEntries = pontoonEntries.filter(row=>isDashboardEnded(row)
    && playData[row.entry_id]?.entry?.pontoon_matchday?.some(result=>result.outcome!=null));
  const pastPontoonEntry = pastPontoonEntries.find(row=>row.entry_id===dashEntryByComp[row.competition_id]) || pastPontoonEntries[0];
  const filteredPrivateComps = sortCompetitions(filterCompetitions(myPrivateComps, privateFilter));

  // Fetch user's squads for invitation
  const fetchUserSquads = async () => {
    if (!session?.user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('squad_members')
        .select(`
          squad_id,
          squads (
            id,
            name
          )
        `)
        .eq('user_id', session.user.id);

      if (error) throw error;
      
      const squads = data?.map(item => ({
        id: item.squads.id,
        name: item.squads.name
      })) || [];
      
      setUserSquads(squads);
    } catch (error) {
    }
  };

  // Fetch pending competition invitations
  const fetchPendingInvitations = async () => {
    if (!session?.user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('competition_invitations')
        .select(`
          id,
          created_at,
          invited_by,
          competitions!competition_invitations_competition_id_fkey (
            id,
            name,
            league,
            season,
            type,
            visibility,
            is_18_plus
          )
        `)
        .eq('invited_user_id', session.user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Fetch inviter usernames separately
      if (data && data.length > 0) {
        const inviterIds = [...new Set(data.map(inv => inv.invited_by))];
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', inviterIds);

        if (profileError) {
        }

        // Map inviter data to invitations
        const profileMap = {};
        profiles?.forEach(p => {
          profileMap[p.id] = p;
        });

        const enrichedData = data.map(inv => ({
          ...inv,
          inviter: profileMap[inv.invited_by] || { username: 'Unknown' }
        }));

        setPendingInvitations(enrichedData);
      } else {
        setPendingInvitations([]);
      }
    } catch (error) {
    }
  };

  // Handle accepting a competition invitation
  const handleAcceptInvitation = async (invitationId, competitionId) => {
    try {
      // First, fetch the competition details to get required fields
      const { data: competition, error: compError } = await supabase
        .from('competitions')
        .select('league, season, start_match_day, is_18_plus, requires_approval')
        .eq('id', competitionId)
        .single();

      if (compError) {
        throw compError;
      }

      const ageCheck = await userMeetsCompetitionAgeRequirement(session.user.id, competition);
      if (!ageCheck.allowed) {
        notify(ageCheck.message, 'error');
        return;
      }

      // Create user selection to join the competition with all required fields
      const { error: insertError } = await supabase
        .from('user_selections')
        .insert({
          user_id: session.user.id,
          competition_id: competitionId,
          league: competition.league,
          season: competition.season,
          match_day: competition.start_match_day,
          status: competition.requires_approval ? 'pending' : 'active'
        });

      if (insertError) {
        // If already joined (23505 = unique violation), just update invitation
        if (insertError.code !== '23505') {
          throw insertError;
        }
      }

      // Update invitation status after successful registration
      const { error: updateError } = await supabase
        .from('competition_invitations')
        .update({ 
          status: 'accepted',
          responded_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      if (updateError) {
        throw updateError;
      }

      notify('Invitation accepted! You\'ve joined the competition.', 'success');
      
      // Refresh invitations list to remove the accepted one
      fetchPendingInvitations();
      
      // Reload the page to refresh all competition data
      window.location.reload();
    } catch (error) {
      notify('Failed to accept invitation. Please try again.', 'error');
    }
  };

  // Handle declining a competition invitation
  const handleDeclineInvitation = async (invitationId) => {
    try {
      const { error } = await supabase
        .from('competition_invitations')
        .update({ 
          status: 'declined',
          responded_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      if (error) throw error;

      notify('Invitation declined.', 'info');
      fetchPendingInvitations();
    } catch (error) {
      notify('Failed to decline invitation. Please try again.', 'error');
    }
  };

  // Open modal with sensible defaults for non-admin users
  const openPrivateCompModal = async () => {
    if (userRole !== "admin") {
      setPrivateCompForm((prev) => ({
        ...prev,
        league: "Premier League",
        season: "2026/27",
        type: "knockout",
        is_18_plus: false,
        inviteSquads: false,
        selectedSquads: [],
      }));
    } else {
      setPrivateCompForm((prev) => ({
        ...prev,
        is_18_plus: false,
        inviteSquads: false,
        selectedSquads: [],
      }));
    }
    await fetchUserSquads();
    setShowPrivateCompModal(true);
  };

  // Fetch future matchdays when league/season changes
  useEffect(() => {
    const fetchFutureMatchdays = async () => {
      const selectedLeague = privateCompForm.league;
      const selectedSeason = privateCompForm.season;

      if (!privateCompForm.league || !privateCompForm.season) {
        setFutureMatchdays([]);
        setPrivateCompForm(prev => ({ ...prev, startMatchDay: "" }));
        return;
      }
      
      const { data } = await supabase
        .from('matchdays_view')
        .select('match_day, first_kickoff_time')
        .eq('league', selectedLeague)
        .eq('season', selectedSeason)
        .gt('first_kickoff_time', new Date().toISOString())
        .order('match_day', { ascending: true });

      if (selectedLeague !== privateCompForm.league || selectedSeason !== privateCompForm.season) {
        return;
      }
      
      if (data && data.length > 0) {
        setFutureMatchdays(data);

        const availableMatchDays = new Set(data.map(md => Number(md.match_day)));
        const currentStartMatchDay = Number(privateCompForm.startMatchDay);
        const hasValidSelection =
          Number.isInteger(currentStartMatchDay) && availableMatchDays.has(currentStartMatchDay);

        if (!hasValidSelection) {
          setPrivateCompForm(prev => ({ ...prev, startMatchDay: data[0].match_day }));
        }
      } else {
        setFutureMatchdays([]);
        setPrivateCompForm(prev => ({ ...prev, startMatchDay: "" }));
      }
    };
    
    fetchFutureMatchdays();
  }, [privateCompForm.league, privateCompForm.season]);

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.user?.id) {
        setLoading(false);
        return;
      }

      // Secondary trophy totals must not hold up loading the player's games.
      void fetchWinStats();

      // Execute all main queries in parallel
      const [
        upcomingResult,
        registeredResult,
        privateCompsResult
      ] = await Promise.allSettled([
        // Fetch upcoming competitions
        supabase
          .from("competitions")
          .select("id, name, league, type, season, start_match_day, player_count, registration_status, visibility, is_18_plus, status, is_multi_league")
          .eq("registration_status", "open")
          .eq("visibility", "public")
          .neq("type", "scout")
          .order("start_date", { ascending: true }),
        // Fetch registered competitions and stats (all statuses)
        supabase
          .from("user_competition_stats_all_view")
          .select("competition_id, competition, competition_type, first_kickoff_time, rank, wins, draws, losses, goals_for, goals_against, points, has_selection, selection_status, competition_status, entry_id, entry_index, entry_label")
          .eq("user_id", session.user.id)
          .neq("competition_type", "scout"),
        // Fetch private competitions created by the user
        supabase
          .from("competitions")
          .select("id, name, registration_key, league, season, player_count, registration_status, status")
          .eq("created_by", session.user.id)
          .eq("visibility", "private")
      ]);

      // Process upcoming competitions
      setDiscoveryFailed(upcomingResult.status !== 'fulfilled' || !!upcomingResult.value.error);
      let upcomingWithKickoff = [];
      if (upcomingResult.status === 'fulfilled' && upcomingResult.value.data) {
        const upcomingData = upcomingResult.value.data;
        
        // Create unique league/season/matchday combinations to batch fetch kickoff times
        const matchdayQueries = new Set();
        upcomingData.forEach(comp => {
          matchdayQueries.add(`${comp.league}|${comp.season}|${comp.start_match_day}`);
        });

        // Batch fetch all needed matchday kickoff times
        const kickoffPromises = Array.from(matchdayQueries).map(key => {
          const [league, season, matchDay] = key.split('|');
          return supabase
            .from("matchdays_view")
            .select("league, season, match_day, first_kickoff_time")
            .eq("league", league)
            .eq("season", season)
            .eq("match_day", parseInt(matchDay))
            .single();
        });

        const kickoffResults = await Promise.allSettled(kickoffPromises);
        
        // Create lookup map for kickoff times
        const kickoffMap = {};
        kickoffResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value.data) {
            const data = result.value.data;
            const key = `${data.league}|${data.season}|${data.match_day}`;
            kickoffMap[key] = data.first_kickoff_time;
          }
        });

        // Merge kickoff times with competition data
        upcomingWithKickoff = upcomingData.map(comp => ({
          ...comp,
          first_kickoff_time: kickoffMap[`${comp.league}|${comp.season}|${comp.start_match_day}`] || null
        }));

        const leagueNames = [...new Set(upcomingData.map((comp) => comp.league).filter(Boolean))];
        if (leagueNames.length > 0) {
          const { data: leaguesData } = await supabase
            .from("api_football_leagues")
            .select("league_name, logo, flag")
            .in("league_name", leagueNames);

          const leagueMediaMap = {};
          for (const league of leaguesData || []) {
            leagueMediaMap[league.league_name] = {
              logo: league.logo,
              flag: league.flag,
            };
          }

          upcomingWithKickoff = upcomingWithKickoff.map((comp) => ({
            ...comp,
            league_logo_url: leagueMediaMap[comp.league]?.logo || null,
            league_flag_url: leagueMediaMap[comp.league]?.flag || null,
          }));
        }
      }
      setUpcoming(upcomingWithKickoff);

      // Process registered competitions
      let regDataRaw = [];
      if (registeredResult.status === 'fulfilled' && registeredResult.value.data) {
        regDataRaw = registeredResult.value.data;
      }

      // Resolve every owned entry, including inactive entries missing from the stats view.
      // Failure never turns an unread pick ledger into a missing-pick action.
      let ownedCompetitions = null;
      try {
        const play = await loadDashboardPlay(supabase, session.user.id, regDataRaw);
        regDataRaw = play.rows;
        ownedCompetitions = play.competitions;
        setPlayData(play.byEntry);
        setGamesFailed(registeredResult.status !== 'fulfilled' || !!registeredResult.value.error);
      } catch {
        setPlayData({});
        setGamesFailed(true);
      }
      regDataRaw = regDataRaw.filter(row => PONTOON_ENABLED || row.competition_type !== 'pontoon');

      setRegistered(regDataRaw);

      // Build "recently finished" summary so ended competitions don't silently disappear.
      // Use registered rows first, then fall back to user_selections in case stats view is empty.
      let recentlyFinishedSourceIds = [...new Set(regDataRaw.map((row) => row.competition_id).filter(Boolean))];
      if (recentlyFinishedSourceIds.length === 0) {
        try {
          const { data: selectionRows = [] } = await supabase
            .from('user_selections')
            .select('competition_id')
            .eq('user_id', session.user.id);

          recentlyFinishedSourceIds = [...new Set(
            (selectionRows || []).map((row) => row.competition_id).filter(Boolean)
          )];
        } catch (_selectionIdFallbackErr) {
          recentlyFinishedSourceIds = [];
        }
      }

      if (recentlyFinishedSourceIds.length > 0) {
        try {
          // Reuse the play snapshot so the collections cannot read different lifecycle states.
          const { data: inactiveCompetitions = [] } = ownedCompetitions
            ? {data:ownedCompetitions.filter(isDashboardEnded)}
            : await supabase
            .from("competitions")
            .select("id, name, league, season, type, status, end_reason, knockout_winner, player_count, visibility, is_18_plus, completed_at, is_multi_league")
            .in("id", recentlyFinishedSourceIds)
            .neq("type", "scout")
            .in("status", ["inactive", "ended", "completed"]);

          if (inactiveCompetitions.length > 0) {
            const inactiveIds = inactiveCompetitions.map((comp) => comp.id);

            const winnerUserIds = [...new Set(
              inactiveCompetitions
                .map((comp) => comp.knockout_winner)
                .filter(Boolean)
            )];

            const winnerNameMap = {};
            if (winnerUserIds.length > 0) {
              const { data: winnerProfiles = [] } = await supabase
                .from("profiles")
                .select("id, username")
                .in("id", winnerUserIds);

              winnerProfiles.forEach((profile) => {
                winnerNameMap[profile.id] = profile.username || "Unknown";
              });
            }

            // Fallback winner lookup for season/world_stage via top-ranked standings row.
            // Prefer all-status view, then fall back to active-only view if migration is not applied yet.
            let standingsRows = [];
            const { data: allStatusRows, error: allStatusError } = await supabase
              .from("user_competition_stats_all_view")
              .select("competition_id, username, rank, points")
              .in("competition_id", inactiveIds)
              .order("rank", { ascending: true });

            if (!allStatusError) {
              standingsRows = allStatusRows || [];
            } else {
              const { data: fallbackRows = [] } = await supabase
                .from("user_competition_stats_view")
                .select("competition_id, username, rank, points")
                .in("competition_id", inactiveIds)
                .order("rank", { ascending: true });

              standingsRows = fallbackRows;
            }

            const topRankByCompetition = {};
            (standingsRows || []).forEach((row) => {
              if (!row?.competition_id) return;
              if (!topRankByCompetition[row.competition_id]) {
                topRankByCompetition[row.competition_id] = row;
              }
            });

            const finishedLeagueNames = [...new Set(
              inactiveCompetitions.map((comp) => comp.league).filter(Boolean)
            )];
            const finishedLeagueMediaMap = {};
            if (finishedLeagueNames.length > 0) {
              const { data: finishedLeaguesData } = await supabase
                .from("api_football_leagues")
                .select("league_name, logo, flag")
                .in("league_name", finishedLeagueNames);

              for (const league of finishedLeaguesData || []) {
                finishedLeagueMediaMap[league.league_name] = {
                  logo: league.logo,
                  flag: league.flag,
                };
              }
            }

            const endedRows = dashboardFinishedRows(inactiveCompetitions.filter(comp=>comp.type!=='scout' && (PONTOON_ENABLED || comp.type!=='pontoon')))
              .map((comp) => {
                const hasNoWinner = comp.end_reason === "all_eliminated";
                const explicitWinner = comp.knockout_winner ? winnerNameMap[comp.knockout_winner] : null;
                const selfWinner = comp.knockout_winner && comp.knockout_winner === session.user.id
                  ? (username || "You")
                  : null;
                const rankedWinner = comp.type==='pontoon'?null:topRankByCompetition[comp.id]?.username || null;
                const winnerName = hasNoWinner ? null : (explicitWinner || selfWinner || rankedWinner);

                return {
                  id: comp.id,
                  name: comp.name,
                  type: comp.type,
                  league: comp.league,
                  season: comp.season,
                  player_count: comp.player_count,
                  visibility: comp.visibility,
                  is_18_plus: comp.is_18_plus,
                  is_multi_league: comp.is_multi_league,
                  league_logo_url: finishedLeagueMediaMap[comp.league]?.logo || null,
                  league_flag_url: finishedLeagueMediaMap[comp.league]?.flag || null,
                  endReason: comp.end_reason,
                  winnerName,
                };
              });

            setRecentlyFinished(endedRows);
          } else {
            setRecentlyFinished([]);
          }
        } catch (_err) {
          setRecentlyFinished([]);
        }
      } else {
        setRecentlyFinished([]);
      }

      // Process private competitions
      const privateComps = (privateCompsResult.status === 'fulfilled' && privateCompsResult.value.data)
        ? privateCompsResult.value.data
        : [];
      setMyPrivateComps(privateComps);

      // Phase 3: entries awaiting approval in competitions this user manages
      // (owns — ANY visibility, incl. public comps created by the FC admin — or
      // admins). Surfaced as per-row badges plus a summary banner (so public
      // owned comps, which have no dashboard row, are still shown).
      try {
        const [ownedRes, adminRes] = await Promise.allSettled([
          supabase.from('competitions').select('id').eq('created_by', session.user.id),
          supabase.from('competition_admins').select('competition_id').eq('user_id', session.user.id).eq('role', 'admin'),
        ]);
        const ownedIds = ownedRes.status === 'fulfilled' ? (ownedRes.value.data || []).map((r) => r.id) : [];
        const adminIds = adminRes.status === 'fulfilled' ? (adminRes.value.data || []).map((r) => r.competition_id) : [];
        const managedIds = [...new Set([...ownedIds, ...adminIds].filter(Boolean))];

        const counts = {};
        let summary = [];
        if (managedIds.length > 0) {
          const { data: pendingRows } = await supabase
            .from('user_selections')
            .select('competition_id')
            .in('competition_id', managedIds)
            .eq('status', 'pending');
          (pendingRows || []).forEach((r) => {
            counts[r.competition_id] = (counts[r.competition_id] || 0) + 1;
          });
          const compIdsWithPending = Object.keys(counts);
          if (compIdsWithPending.length > 0) {
            const { data: compMeta } = await supabase
              .from('competitions')
              .select('id, name, type')
              .in('id', compIdsWithPending);
            summary = (compMeta || []).map((c) => ({
              competition_id: c.id,
              name: c.name,
              type: c.type,
              count: counts[c.id],
            }));
          }
        }
        setPendingApprovalsByComp(counts);
        setPendingApprovalSummary(summary);
      } catch (e) {
        setPendingApprovalsByComp({});
        setPendingApprovalSummary([]);
      }

      // Fetch user's referral code for sharing
      const { data: referralData } = await supabase
        .from('user_referral_codes')
        .select('referral_code')
        .eq('user_id', session.user.id)
        .single();
      
      if (referralData?.referral_code) {
        setUserReferralCode(referralData.referral_code);
      }

      setLoading(false);
    };
    // Refresh the existing page reads on focus/visibility return, coalescing events.
    // No provider calls, polling, subscriptions or overlapping page loads.
    let disposed = false, refreshing = false, queued = false;
    const refresh = async () => {
      if(disposed || document.visibilityState==='hidden')return;
      if(refreshing){queued=true;return;}
      refreshing=true;
      try {await fetchData();}
      catch {if(!disposed){setGamesFailed(true);setDiscoveryFailed(true);setLoading(false);}}
      finally {refreshing=false;if(queued&&!disposed){queued=false;void refresh();}}
    };
    void refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return ()=>{disposed=true;window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',refresh);};
  }, [session?.user?.id]);

  // Auto-join competition if user came from invite link
  useEffect(() => {
    // Don't run if still loading initial data
    if (loading) {
      return;
    }

    const handleAutoJoin = async (joinKey) => {
      
      try {
        // Fetch competition by join_key (include all fields needed for user_selections insert)
        const { data: competition, error: compError } = await supabase
          .from('competitions')
          .select('id, name, type, status, registration_status, player_count, league, season, start_match_day, is_18_plus, requires_approval')
          .eq('registration_key', joinKey)
          .single();

        if (compError || !competition) {
          notify('Invalid join link', 'error');
          localStorage.removeItem('pendingJoin');
          return;
        }

        // Check if competition is still accepting entries
        if (competition.registration_status !== 'open' || competition.status === 'ended') {
          notify(`${competition.name} is no longer accepting entries`, 'error');
          localStorage.removeItem('pendingJoin');
          return;
        }

        const ageCheck = await userMeetsCompetitionAgeRequirement(session.user.id, competition);
        if (!ageCheck.allowed) {
          notify(ageCheck.message, 'error');
          localStorage.removeItem('pendingJoin');
          return;
        }

        // Check if user is already a member (limit(1) keeps this safe under multi-entry)
        const { data: existingEntry, error: existingError } = await supabase
          .from('user_selections')
          .select('id')
          .eq('competition_id', competition.id)
          .eq('user_id', session.user.id)
          .limit(1)
          .maybeSingle();

        if (existingEntry) {
          notify(`You're already a member of ${competition.name}`, 'info');
          localStorage.removeItem('pendingJoin');
          return;
        }

        // Join the competition
        const { data: insertData, error: insertError } = await supabase
          .from('user_selections')
          .insert({
            competition_id: competition.id,
            user_id: session.user.id,
            league: competition.league,
            season: competition.season,
            match_day: competition.start_match_day,
            status: competition.requires_approval ? 'pending' : 'active'
          })
          .select();

        if (insertError) {
          if (isAlreadyRegisteredError(insertError)) {
            notify(`You're already a member of ${competition.name}`, 'info');
            localStorage.removeItem('pendingJoin');
            return;
          }
          notify('Failed to join competition', 'error');
          localStorage.removeItem('pendingJoin');
          return;
        }

        // Success!
        notify(
          competition.requires_approval
            ? `Request to join ${competition.name} sent — awaiting approval.`
            : `Welcome to ${competition.name}! 🎉`,
          'success'
        );
        localStorage.removeItem('pendingJoin');

        // Refresh the dashboard data
        window.location.reload();
      } catch (err) {
        notify('Something went wrong joining the competition', 'error');
        localStorage.removeItem('pendingJoin');
      }
    };

    const pendingJoin = localStorage.getItem('pendingJoin');
    
    if (pendingJoin && session?.user?.id) {
      handleAutoJoin(pendingJoin);
    }
  }, [session?.user?.id, loading]);

  // Fetch pending invitations on mount
  useEffect(() => {
    if (session?.user?.id && !loading) {
      fetchPendingInvitations();
    }
  }, [session?.user?.id, loading]);


  async function handleCreatePrivateCompetition(e) {
    e.preventDefault();
    setCreatingPrivateComp(true);
    
    // Generate a simple registration key (8 random alphanumeric characters)
    const generateRegistrationKey = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing chars like 0/O, 1/I
      let key = '';
      for (let i = 0; i < 8; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return key;
    };
    
    // Check for duplicate competition name (client-side)
    const { data: existingComps } = await supabase
      .from("competitions")
      .select("id")
      .eq("name", privateCompForm.name)
      .eq("type", privateCompForm.type);
    if (existingComps && existingComps.length > 0) {
      notify("A competition with this name and type already exists.", 'info');
      setCreatingPrivateComp(false);
      return;
    }
    // Validate start match day is a positive integer (skip for World Cup / world_stage)
    const isWorldCup = privateCompForm.league === 'World Cup' || privateCompForm.type === 'world_stage';
    const ml = privateCompForm.multiLeague || { enabled: false, leagues: [], rounds: [] };
    const isMultiLeague = MULTI_LEAGUE_ENABLED && privateCompForm.type === 'knockout' && ml.enabled;
    // Pontoon: a free side-game attached to a single-league Knockout (not multi-league / World Cup).
    const pontoonCfg = privateCompForm.pontoon || { enabled: false, matchDays: "" };
    const addPontoon = PONTOON_ENABLED && privateCompForm.type === 'knockout' && !isMultiLeague && pontoonCfg.enabled;
    let pontoonMatchDaysInt = null;
    if (addPontoon) {
      pontoonMatchDaysInt = parseInt(String(pontoonCfg.matchDays || '').replace(/\D/g, ''), 10);
      if (!pontoonMatchDaysInt || pontoonMatchDaysInt < 1) {
        notify('Pontoon match days must be a positive whole number.', 'info');
        setCreatingPrivateComp(false);
        return;
      }
    }
    let matchDayInt = null;
    if (!isWorldCup && !isMultiLeague) {
      const rawMatchDay = privateCompForm.startMatchDay;
      matchDayInt = typeof rawMatchDay === 'number'
        ? Math.floor(rawMatchDay)
        : parseInt(String(rawMatchDay || '').replace(/\D/g, ''), 10);
      if (!matchDayInt || matchDayInt < 1) {
        notify('Start Match Day must be a positive whole number.', 'info');
        setCreatingPrivateComp(false);
        return;
      }
    }

    // Multi-league validation (Knockout MVP)
    if (isMultiLeague) {
      const mlLeagues = ml.leagues || [];
      const mlRounds = ml.rounds || [];
      if (mlLeagues.length < 1) {
        notify('Select at least one league for a multi-league competition.', 'info');
        setCreatingPrivateComp(false);
        return;
      }
      if (mlRounds.length < 1) {
        notify('Add at least one round.', 'info');
        setCreatingPrivateComp(false);
        return;
      }
      const emptyRound = mlRounds.findIndex((r) => !r || Object.keys(r).length === 0);
      if (emptyRound !== -1) {
        notify(`Round ${emptyRound + 1} needs at least one league's matchday.`, 'info');
        setCreatingPrivateComp(false);
        return;
      }
    }
    // Prepare payload — honour the user's own league/season/type picks.
    const leagueToUse = privateCompForm.league;
    const seasonToUse = privateCompForm.season;
    const typeToUse = privateCompForm.type;

    // Top Gaffer client-side validation (the RPC re-validates authoritatively).
    const tg = privateCompForm.topGaffer || { enabled: false };
    const tgEnabled = typeToUse === 'season' && tg.enabled;
    if (tgEnabled) {
      const available = futureMatchdays.map((m) => m.match_day).filter((md) => md >= matchDayInt);
      const stages = tg.stages || [];
      const allocated = new Set(stages.flat());
      if (stages.length < 1 || stages.some((s) => !s || s.length === 0)) {
        notify('Each Top Gaffer stage must contain at least one matchday.', 'info');
        setCreatingPrivateComp(false);
        return;
      }
      if (allocated.size !== available.length || available.some((md) => !allocated.has(md))) {
        notify('Assign every matchday to a Top Gaffer stage before creating the competition.', 'info');
        setCreatingPrivateComp(false);
        return;
      }
    }

    const payload = {
      name: privateCompForm.name,
      league: isMultiLeague ? ml.leagues[0] : leagueToUse,
      season: seasonToUse,
      type: typeToUse,
      revival_strategy: privateCompForm.knockoutType,
      start_date: privateCompForm.startDate,
      start_match_day: (isWorldCup || isMultiLeague) ? 1 : matchDayInt,
      is_18_plus: privateCompForm.is_18_plus,
      created_by: session?.user?.id,
      registration_status: "open",
      visibility: "private",
      registration_key: generateRegistrationKey(),
      ...(isMultiLeague ? { is_multi_league: true, current_match_day: 1 } : {}),
    };
    const { data, error } = await supabase.from("competitions").insert([
      payload
    ]).select();
    setCreatingPrivateComp(false);
    if (error || !data || !data[0]) {
      notify(`Failed to create private competition: ${error?.message || 'Unknown error'}`, 'error');
      return;
    }
    
    const newCompetition = data[0];
    setPrivateCompKey(newCompetition.registration_key || "");

    // Multi-league: write the round -> league-matchday mapping rows.
    if (isMultiLeague) {
      const rows = [];
      (ml.rounds || []).forEach((roundMap, idx) => {
        Object.entries(roundMap || {}).forEach(([lg, md]) => {
          if (md != null && md !== "") {
            rows.push({
              competition_id: newCompetition.id,
              round_number: idx + 1,
              league: lg,
              season: seasonToUse,
              match_day: Number(md),
            });
          }
        });
      });
      if (rows.length > 0) {
        const { error: mlError } = await supabase.from('competition_round_matchdays').insert(rows);
        if (mlError) {
          notify(`Competition created, but multi-league round setup failed: ${mlError.message}`, 'error');
        }
      }
    }

    // Pontoon: create the linked child competition (type='pontoon'). It shares the
    // parent's league/season and runs for pontoonMatchDaysInt match days. Entrants
    // are auto-enrolled when their parent entry is eliminated (DB trigger, Phase 6c);
    // it is not publicly joinable, so registration is closed. Non-fatal on failure —
    // the parent Knockout already exists.
    if (addPontoon) {
      const pontoonPayload = {
        name: `${privateCompForm.name} — Pontoon`,
        league: leagueToUse,
        season: seasonToUse,
        type: 'pontoon',
        revival_strategy: 'elimination',
        start_date: privateCompForm.startDate,
        start_match_day: matchDayInt || 1,
        is_18_plus: privateCompForm.is_18_plus,
        created_by: session?.user?.id,
        registration_status: 'closed',
        visibility: 'private',
        registration_key: generateRegistrationKey(),
        parent_competition_id: newCompetition.id,
        pontoon_match_days: pontoonMatchDaysInt,
      };
      const { error: pontoonError } = await supabase.from('competitions').insert([pontoonPayload]);
      if (pontoonError) {
        notify(`Competition created, but Pontoon setup failed: ${pontoonError.message}`, 'error');
      }
    }

    // Save Top Gaffer config via the validated RPC (competition must exist first).
    if (tgEnabled) {
      const p_stages = (tg.stages || []).map((mds, i) => ({ stage_number: i + 1, matchdays: mds }));
      const { error: tgError } = await supabase.rpc('save_top_gaffer_config', {
        p_competition_id: newCompetition.id,
        p_enabled: true,
        p_stages,
      });
      if (tgError) {
        notify(`Competition created, but Top Gaffer setup failed: ${tgError.message}`, 'error');
      }
    }
    
    // Send squad invitations if enabled
    if (privateCompForm.inviteSquads && privateCompForm.selectedSquads.length > 0) {
      try {
        // Get all squad members from selected squads
        const { data: squadMembers, error: membersError } = await supabase
          .from('squad_members')
          .select('user_id')
          .in('squad_id', privateCompForm.selectedSquads);

        if (membersError) throw membersError;

        // Get unique user IDs (excluding the creator)
        const uniqueUserIds = [...new Set(
          squadMembers?.map(m => m.user_id).filter(id => id !== session.user.id) || []
        )];

        if (uniqueUserIds.length > 0) {
          // Check for existing invitations to avoid duplicates
          const { data: existingInvites } = await supabase
            .from('competition_invitations')
            .select('invited_user_id')
            .eq('competition_id', newCompetition.id)
            .in('invited_user_id', uniqueUserIds);

          const existingUserIds = new Set(existingInvites?.map(inv => inv.invited_user_id) || []);
          const usersToInvite = uniqueUserIds.filter(id => !existingUserIds.has(id));

          if (usersToInvite.length > 0) {
            // Create invitation records for users who don't already have invitations
            const invitations = usersToInvite.map(userId => ({
              competition_id: newCompetition.id,
              invited_user_id: userId,
              invited_by: session.user.id,
              status: 'pending'
            }));

            // Insert in chunks — a squad can now be large (friend cap raised to
            // 5000), and one giant insert risks request-size/timeout limits.
            const CHUNK = 500;
            let inviteError = null;
            for (let i = 0; i < invitations.length; i += CHUNK) {
              const { error } = await supabase
                .from('competition_invitations')
                .insert(invitations.slice(i, i + CHUNK));
              if (error) { inviteError = error; break; }
            }

            if (inviteError) {
              notify(`Competition created, but failed to send ${usersToInvite.length} invitation(s)`, 'warning');
            } else {
              const skippedCount = uniqueUserIds.length - usersToInvite.length;
              if (skippedCount > 0) {
                notify(`Competition created! Sent ${usersToInvite.length} new invitation(s) (${skippedCount} already invited)`, 'success');
              } else {
                notify(`Competition created and ${usersToInvite.length} invitation(s) sent!`, 'success');
              }
            }
          } else {
            notify('Competition created! All squad members were already invited.', 'info');
          }
        } else {
          notify("Private competition created successfully!", 'success');
        }
      } catch (error) {
        notify("Competition created, but failed to send invitations", 'warning');
      }
    } else {
      notify("Private competition created successfully!", 'success');
    }
    
    // Clear form after successful creation
    setPrivateCompForm({
      name: "",
      league: "",
      season: "",
      knockoutType: "elimination",
      type: "",
      startDate: "",
      startMatchDay: "",
      is_18_plus: false,
      inviteSquads: false,
      selectedSquads: [],
      topGaffer: { enabled: false, stageCount: 1, stages: [] },
      multiLeague: { enabled: false, leagues: [], rounds: [] },
      pontoon: { enabled: false, matchDays: "" },
    });

    // Close the modal and surface the newly created competition immediately
    // (the registration key is available in the list with a copy button).
    setShowPrivateCompModal(false);
    setPrivateCompKey("");
    setMyPrivateComps((prev) => [newCompetition, ...(prev || [])]);
  }

  // Copy registration key to clipboard
  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      notify("Registration key copied to clipboard!", "success");
    } catch {
      notify("Failed to copy registration key", "error");
    }
  };

  // Social sharing helpers
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-24 md:pb-8 fc-workspace">
          <LoadingState label="Loading dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      {/* Responsive green banner for mobile, like login page */}
      <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-30">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-24 md:pb-8 fc-workspace">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h1>Dashboard</h1>
          <Link to="/trophy-room" className="text-sm font-semibold text-action-primary hover:underline">Trophy Room</Link>
        </div>
        <DashboardNextMatchday rows={registered} playData={playData} failed={gamesFailed} now={now}/>
        <DashboardGames groups={groupedRegistered} selectedRowFor={selectedRowFor} setDashEntry={setDashEntry}
          playData={playData} filter={registeredFilter} setFilter={setRegisteredFilter} failed={gamesFailed} now={now}
          pendingApprovalsByComp={pendingApprovalsByComp}/>
        <DashboardDiscovery competitions={upcoming} getRoute={getCompetitionRoute} failed={discoveryFailed} now={now}/>

        {/* Entries awaiting your approval (competitions you own/admin, incl. public) */}
        {pendingApprovalSummary.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">⏳</span>
                <h2 className="font-semibold text-amber-800 text-base sm:text-lg">Entries awaiting your approval</h2>
              </div>
              <ul className="space-y-1">
                {pendingApprovalSummary.map((c) => (
                  <li key={c.competition_id}>
                    <Link
                      to={getCompetitionRoute(c.competition_id, c.type)}
                      className="text-sm text-amber-800 hover:underline font-medium"
                    >
                      {c.name} — {c.count} {c.count === 1 ? 'entry' : 'entries'} to review
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Pending Competition Invitations */}
        {pendingInvitations.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <h2 className="font-semibold text-brand-text text-lg sm:text-xl mb-3 sm:mb-4">
              Competition Invitations <span className="text-action-primary">({pendingInvitations.length})</span>
            </h2>
            <div className="fc-competition-grid">
              {pendingInvitations.slice(0, 10).map((invitation) => (
                <FcCard as="div" padding="standard"
                  key={invitation.id}
                  className="min-w-[280px] max-w-xs w-full bg-surface-card rounded-lg shadow-md p-4 flex flex-col justify-between border-l-4 border-ui-border"
                >
                  <div className="flex-1 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-base text-content-primary">
                        {invitation.competitions.name}
                      </h3>
                      {invitation.competitions.is_18_plus && (
                        <span className="px-2 py-0.5 rounded bg-red-100 text-ui-error text-xs font-bold border border-red-200">18+</span>
                      )}
                    </div>
                    <div className="text-sm text-content-secondary space-y-1">
                      <p>
                        <span className="font-semibold">{invitation.competitions.league}</span> • {invitation.competitions.season}
                      </p>
                      <p>
                        Invited by <span className="font-semibold">@{invitation.inviter.username}</span>
                      </p>
                      <p className="text-xs text-content-secondary">
                        {new Date(invitation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <FcButton variant="tertiary" type="submit"
                      onClick={() => handleAcceptInvitation(invitation.id, invitation.competitions.id)}
                      className="flex-1"
                    >
                      Accept
                    </FcButton>
                    <FcButton variant="tertiary" type="submit"
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      className="flex-1"
                    >
                      Decline
                    </FcButton>
                  </div>
                </FcCard>
              ))}
            </div>
          </section>
        )}

        <CompetitionCarousel id="dashboard-finished-rail" label="Recently finished competition carousel"
          heading={<>Recently Finished <span className="text-action-primary">({recentlyFinished.length})</span></>}
          actions={<FcButton aria-pressed={registeredFilter === 'inactive'} variant="tertiary" onClick={()=>setRegisteredFilter('inactive')}>Show Inactive</FcButton>}
          previousLabel="Previous finished competitions" nextLabel="Next finished competitions" items={recentlyFinished}
          empty={<div className="w-full bg-gray-100 text-content-secondary rounded-lg p-4 text-sm sm:text-base">No recently finished competitions yet. Results will appear here when your games end.</div>}
          renderCard={comp=><CompetitionCard comp={comp} joined canRegister={false} getRoute={()=>dashboardResultsRoute({competition_id:comp.id,competition_type:comp.type,entry_id:dashEntryByComp[comp.id] || registered.find(row=>row.competition_id===comp.id)?.entry_id})}
            compact forceViewAction leagueBranding statusBadgeLabel="Ended" hideCountdown hideStats
            winnerLabel={comp.winnerName || (comp.endReason==='all_eliminated'?'No winner':'')} actionLabel="View Final Results"/>}/>

        {PONTOON_ENABLED && (
          <section className="mb-6" aria-labelledby="dashboard-pontoon-title">
            <h2 id="dashboard-pontoon-title" className="font-semibold mb-3 text-brand-text text-lg sm:text-xl">{pontoonGames.length?'Your Pontoon Games':'No active Pontoon entries'}</h2>
            {!pontoonGames.length?<FcCard>
              <p>{hasEndedPontoonGames?'Your previous games have ended. You can still view their results.':'You don’t have any active Pontoon entries.'}</p>
              {pastPontoonEntry&&<Link className="text-action-primary font-semibold hover:underline" to={dashboardResultsRoute(pastPontoonEntry)}>View past Pontoon results →</Link>}
            </FcCard>:<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pontoonGames.map((p) => {
                return (
                  <FcLinkButton variant="secondary"
                    key={p.entry_id}
                    to={entryRoute(p)}
                    className="block"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-ui-green">{p.competition} · {entryName(p)}</div>
                    </div>
                    <div className="text-sm mt-1 text-action-primary">
                      Play Pontoon →
                    </div>
                  </FcLinkButton>
                );
              })}
            </div>}
          </section>
        )}

        <p className="text-sm text-content-secondary my-6"><Link to="/trophy-room" className="text-action-primary font-semibold hover:underline">Your Trophy Room</Link> · {seasonWins ?? '—'} Season wins · {knockoutWins ?? '—'} Knockout wins</p>

        {/* My Private Competitions - Figma-style table/cards */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 mt-8">
            <h2 className="font-semibold mb-2 sm:mb-0 text-brand-text text-lg">Your Private Competitions</h2>
            
            {/* Filter tabs and create button */}
            <div className="flex flex-wrap items-center gap-4">
              <SegmentedControl label="Private competitions filter" value={privateFilter} onValueChange={setPrivateFilter} options={['active','inactive','all'].map(value=>({value,label:value[0].toUpperCase()+value.slice(1)}))}/>
              
              <FcButton variant="tertiary" type="submit"
                className="hover:underline"
                onClick={() => openPrivateCompModal()}
              >
                + Private Competition
              </FcButton>
            </div>
          </div>
          {/* Table for desktop */}
          <div className="hidden md:block">
            <FcTable label="Your private competitions" className="min-w-[500px] w-full border-collapse border border-brand-border bg-surface-card text-xs sm:text-sm rounded-lg overflow-hidden">
              <thead className="bg-ui-green text-white">
                <tr>
                  <th scope="col" className="border p-2 text-left whitespace-nowrap">Competition</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap">Registered Players</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap">Status</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap">Registration Key</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap">Invite Players</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrivateComps.map((comp) => (
                  <tr key={comp.id} className={comp.status !== 'active' ? 'opacity-50' : ''}>
                    <td className="border p-2 text-left align-top break-words max-w-xs">
                      <Link to={getCompetitionRoute(comp.id, comp.type)} className="text-ui-green font-semibold hover:underline break-words max-w-xs block">{comp.name}</Link>
                      {pendingApprovalsByComp[comp.id] > 0 && (
                        <FcLinkButton variant="secondary"
                          to={getCompetitionRoute(comp.id, comp.type)}
                          className="inline-block mt-1 text-amber-800 border border-amber-300"
                        >
                          ⏳ {pendingApprovalsByComp[comp.id]} awaiting approval
                        </FcLinkButton>
                      )}
                    </td>
                    <td className="border p-2 text-center whitespace-nowrap align-top">{typeof comp.player_count === 'number' ? comp.player_count : '-'}</td>
                    <td className="border p-2 text-center whitespace-nowrap align-top">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${comp.status === 'active' ? "text-ui-success bg-green-100" : "text-content-secondary bg-gray-100"}`}>{comp.status || '-'}</span>
                    </td>
                    <td className="border p-2 text-center whitespace-nowrap font-mono align-top">
                      <span className="font-mono text-xs mr-2">{comp.registration_key}</span>
                      <FcButton variant="tertiary" type="submit" aria-label="Copy" onClick={() => handleCopyKey(comp.registration_key)} title="Copy" className="ml-2 align-middle"><FaRegCopy className="text-content-secondary inline" /></FcButton>
                    </td>
                    <td className="border p-2 text-center whitespace-nowrap align-top">
                      <div className="flex items-center justify-center gap-2">
                        <ShareButton
                          url={`${window.location.origin}/join/${comp.registration_key}${userReferralCode ? `?ref=${userReferralCode}` : ''}`}
                          title={`Join my ${comp.name} competition!`}
                          text={`I'm inviting you to join my private competition '${comp.name}' on Footy Coach.`}
                          type="competition"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </FcTable>
          </div>
          {/* Cards for mobile */}
          <ul className="md:hidden space-y-4 mb-24">
            {filteredPrivateComps.map((comp) => (
              <FcCard as="li" padding="standard" key={comp.id} className={`bg-surface-card rounded-lg shadow p-2 border border-ui-border flex flex-col gap-0 ${comp.status !== 'active' ? 'opacity-50' : ''}`}>
                <div className="flex flex-row justify-between items-center">
                  <Link to={getCompetitionRoute(comp.id, comp.type)} className="font-semibold text-brand-text text-base hover:underline flex-1 truncate">{comp.name}</Link>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-brand-text">Players: <span className="font-mono">{typeof comp.player_count === 'number' ? comp.player_count : '-'}</span></span>
                    <span className={`text-xs font-semibold ${comp.status === 'active' ? "text-ui-success" : "text-content-secondary"}`}>{comp.status || '-'}</span>
                  </div>
                </div>
                {pendingApprovalsByComp[comp.id] > 0 && (
                  <FcLinkButton variant="secondary"
                    to={getCompetitionRoute(comp.id, comp.type)}
                    className="inline-block mt-1 self-start text-amber-800 border border-amber-300"
                  >
                    ⏳ {pendingApprovalsByComp[comp.id]} awaiting approval
                  </FcLinkButton>
                )}
                <div className="flex flex-row items-center justify-between mt-1">
                  <div className="flex items-center">
                    <span className="font-semibold text-xs mr-1">Reg Key:</span>
                    <span className="font-mono text-xs mr-1">{comp.registration_key}</span>
                    <FcButton variant="tertiary" type="submit" aria-label="Copy" onClick={() => handleCopyKey(comp.registration_key)} title="Copy" className="ml-1 align-middle"><FaRegCopy className="text-content-secondary inline" /></FcButton>
                  </div>
                  <ShareButton
                    url={`${window.location.origin}/join/${comp.registration_key}${userReferralCode ? `?ref=${userReferralCode}` : ''}`}
                    title={`Join my ${comp.name} competition!`}
                    text={`I'm inviting you to join my private competition '${comp.name}' on Footy Coach.`}
                    type="competition"
                    className="bg-action-primary hover:bg-[#a95c28] text-white px-3 py-1 rounded text-xs transition-colors"
                  >
                    Share
                  </ShareButton>
                </div>
              </FcCard>
            ))}
          </ul>
        </section>

        {/* Private Competition Modal */}
        {showPrivateCompModal && (
          <FcDialog open title="Create Private Competition" size="standard" onCancel={() => setShowPrivateCompModal(false)}>
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">

              <form onSubmit={handleCreatePrivateCompetition} className="space-y-4">
                <FcInput label="Competition name" aria-label="Competition name"
                  type="text"
                  placeholder="Competition Name"
                  value={privateCompForm.name}
                  onChange={e => setPrivateCompForm({ ...privateCompForm, name: e.target.value })}
                  className="block w-full border"
                  required
                />
                <FcSelect label="Competition type" aria-label="Competition type"
                  value={privateCompForm.type}
                  onChange={e => {
                    const newType = e.target.value;
                    if (newType === 'world_stage') {
                      setPrivateCompForm({ ...privateCompForm, type: newType, league: 'World Cup', season: '2026', startMatchDay: '' });
                    } else {
                      setPrivateCompForm({ ...privateCompForm, type: newType, league: '', season: '' });
                    }
                  }}
                  className="block w-full border"
                  required
                >
                  <option value="" disabled>Select Competition Type</option>
                  <option value="knockout">Knockout</option>
                  <option value="season">Season</option>
                </FcSelect>

                {/* Revival Strategy - Only for Knockout competitions */}
                {privateCompForm.type === 'knockout' && (
                  <div className="p-3 border rounded bg-gray-50">
                    <label className="block text-sm font-semibold mb-2 text-content-primary">Revival Strategy:</label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <FcInput aria-label="Free Pass"
                          type="radio"
                          name="knockoutType"
                          value="free_pass"
                          checked={privateCompForm.knockoutType === "free_pass"}
                          onChange={e => setPrivateCompForm({ ...privateCompForm, knockoutType: e.target.value })}
                          className="mr-2"
                        />
                        <span>Free Pass</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <FcInput aria-label="Elimination"
                          type="radio"
                          name="knockoutType"
                          value="elimination"
                          checked={privateCompForm.knockoutType === "elimination"}
                          onChange={e => setPrivateCompForm({ ...privateCompForm, knockoutType: e.target.value })}
                          className="mr-2"
                        />
                        <span>Elimination</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Multi-league (Knockout only) — gated by MULTI_LEAGUE_ENABLED */}
                {MULTI_LEAGUE_ENABLED && privateCompForm.type === 'knockout' && (
                  <MultiLeagueConfig
                    leagueOptions={LEAGUE_OPTIONS}
                    season={privateCompForm.season}
                    value={privateCompForm.multiLeague}
                    onChange={(ml) => setPrivateCompForm({ ...privateCompForm, multiLeague: ml })}
                  />
                )}

                {/* Pontoon (single-league Knockout only) — gated by PONTOON_ENABLED */}
                {PONTOON_ENABLED && privateCompForm.type === 'knockout' && !privateCompForm.multiLeague?.enabled && (
                  <div className="p-3 border rounded bg-gray-50">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <FcInput aria-label="Add Pontoon (free side-game for knocked-out players)"
                        type="checkbox"
                        checked={!!privateCompForm.pontoon?.enabled}
                        onChange={(e) => setPrivateCompForm({
                          ...privateCompForm,
                          pontoon: { ...(privateCompForm.pontoon || {}), enabled: e.target.checked },
                        })}
                        className="w-4"
                      />
                      <span className="text-sm font-semibold text-content-primary">
                        Add Pontoon (free side-game for knocked-out players)
                      </span>
                    </label>
                    {privateCompForm.pontoon?.enabled && (
                      <div className="mt-3">
                        <label className="block text-sm font-semibold text-content-primary mb-1">
                          Pontoon match days
                        </label>
                        <FcInput aria-label="Pontoon match days"
                          type="number"
                          min={1}
                          max={38}
                          value={privateCompForm.pontoon?.matchDays ?? ""}
                          onChange={(e) => setPrivateCompForm({
                            ...privateCompForm,
                            pontoon: { ...(privateCompForm.pontoon || {}), matchDays: e.target.value },
                          })}
                          className="w-24 border"
                          placeholder="e.g. 7"
                        />
                        <p className="text-xs text-content-secondary mt-1">
                          Eliminated entries are auto-enrolled for the following match day. Pontoon ends
                          after this many match days or when the Knockout ends, whichever is sooner.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {!(privateCompForm.type === 'knockout' && privateCompForm.multiLeague?.enabled) && (
                <FcSelect label="League" aria-label="League"
                    value={privateCompForm.league}
                    onChange={e => setPrivateCompForm({ ...privateCompForm, league: e.target.value })}
                    className="block w-full border"
                    required
                  >
                    {privateCompForm.type === 'world_stage' ? (
                      <option value="World Cup">World Cup</option>
                    ) : (
                      <>
                        <option value="" disabled>Select League</option>
                        <option value="Premier League">Premier League</option>
                        <option value="Championship">Championship</option>
                        <option value="La Liga">La Liga</option>
                        <option value="Serie A">Serie A</option>
                        <option value="Ligue 1">Ligue 1</option>
                        <option value="Bundesliga">Bundesliga</option>
                        <option value="League One">League One</option>
                        <option value="League Two">League Two</option>
                        <option value="MLS">MLS</option>
                      </>
                    )}
                  </FcSelect>
                )}
                <FcSelect label="Season" aria-label="Season"
                    value={privateCompForm.season}
                    onChange={e => setPrivateCompForm({ ...privateCompForm, season: e.target.value })}
                    className="block w-full border"
                    required
                  >
                    {privateCompForm.league === 'World Cup' ? (
                      <option value="2026">2026</option>
                    ) : (
                      <>
                        <option value="" disabled>Select Season</option>
                        <option value="2026/27">2026/27</option>
                      </>
                    )}
                  </FcSelect>
                <FcInput aria-label="Start Date"
                  type="date"
                  placeholder="Start Date"
                  value={privateCompForm.startDate || ""}
                  onChange={e => setPrivateCompForm({ ...privateCompForm, startDate: e.target.value })}
                  className="block w-full border"
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
                {privateCompForm.league !== 'World Cup' && !(privateCompForm.type === 'knockout' && privateCompForm.multiLeague?.enabled) && (
                  <FcSelect aria-label="Select Start Match Day"
                    value={privateCompForm.startMatchDay || ""}
                    onChange={e => setPrivateCompForm({ ...privateCompForm, startMatchDay: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                    className="block w-full border"
                    required
                  >
                    <option value="">Select Start Match Day</option>
                    {futureMatchdays.map((md) => (
                      <option key={md.match_day} value={md.match_day}>
                        Match Day {md.match_day} - {new Date(md.first_kickoff_time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </option>
                    ))}
                  </FcSelect>
                )}

                {privateCompForm.type === 'season' && (
                  <TopGafferConfig
                    availableMatchdays={futureMatchdays.filter(
                      (m) => m.match_day >= (Number(privateCompForm.startMatchDay) || 0)
                    )}
                    value={privateCompForm.topGaffer}
                    onChange={(tg) => setPrivateCompForm({ ...privateCompForm, topGaffer: tg })}
                  />
                )}

                <label className="flex items-center gap-2">
                  <FcInput aria-label="This competition is 18+"
                    type="checkbox"
                    checked={privateCompForm.is_18_plus}
                    onChange={e => setPrivateCompForm({ ...privateCompForm, is_18_plus: e.target.checked })}
                    className="w-4"
                  />
                  <span className="text-sm font-semibold text-content-primary">This competition is 18+</span>
                </label>
                
                {/* Squad Invitation Section */}
                {userSquads.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <label className="flex items-center gap-2 mb-3">
                      <FcInput aria-label="Invite Squad Members"
                        type="checkbox"
                        checked={privateCompForm.inviteSquads}
                        onChange={(e) => setPrivateCompForm({ 
                          ...privateCompForm, 
                          inviteSquads: e.target.checked,
                          selectedSquads: e.target.checked ? privateCompForm.selectedSquads : []
                        })}
                        className="w-4"
                      />
                      <span className="font-semibold text-content-primary">Invite Squad Members</span>
                    </label>
                    
                    {privateCompForm.inviteSquads && (
                      <div className="ml-6">
                        <p className="text-sm text-content-secondary mb-2">Select squads to invite:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {userSquads.map(squad => (
                            <label key={squad.id} className="flex items-center gap-2">
                              <FcInput aria-label="Enable option"
                                type="checkbox"
                                checked={privateCompForm.selectedSquads.includes(squad.id)}
                                onChange={(e) => {
                                  const newSelected = e.target.checked
                                    ? [...privateCompForm.selectedSquads, squad.id]
                                    : privateCompForm.selectedSquads.filter(id => id !== squad.id);
                                  setPrivateCompForm({ ...privateCompForm, selectedSquads: newSelected });
                                }}
                                className="w-4"
                              />
                              <span className="text-sm">{squad.name}</span>
                            </label>
                          ))}
                        </div>
                        {privateCompForm.selectedSquads.length > 0 && (
                          <p className="text-xs text-content-secondary mt-2">
                            {privateCompForm.selectedSquads.length} squad(s) selected
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2 justify-end">
                  <FcButton variant="tertiary"
                    type="button"
                    className=""
                    onClick={() => setShowPrivateCompModal(false)}
                  >
                    Cancel
                  </FcButton>
                  <FcButton variant="primary"
                    type="submit"
                    className=""
                    disabled={creatingPrivateComp}
                  >
                    {creatingPrivateComp ? "Creating..." : "Create"}
                  </FcButton>
                </div>
              </form>
              {privateCompKey && (
                <div className="mt-4 p-3 bg-green-100 rounded text-ui-success">
                  <div className="font-semibold">Registration Key:</div>
                  <div className="font-mono text-lg mb-2">{privateCompKey}</div>
                  <FcButton variant="primary" type="submit"
                    className=""
                    onClick={() => navigator.clipboard.writeText(privateCompKey)}
                  >
                    Copy Key
                  </FcButton>
                </div>
              )}
            </FcCard>
          </FcDialog>
        )}
      </div>
    </div>
  );
}
